import { Button } from '@design-system';
import { openIntercomChat } from '@/util/intercomUtil';
import { Checklist, type ChecklistItem } from '../components/checklist';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { APPLICATION_SECTION_TITLES } from '../components/application-section-frame';
import { useApplication } from '../hooks/use-application';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useConsumerPlan } from '../hooks/use-consumer-plan';
import { usePlanTotals } from '../hooks/use-plan-totals';
import type { ConsumerStepProps } from './step-props';

export function ApplicationStartStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { answers, sections, saveAnswers, selectedQuote } = useApplication(clientId);
  const { summaryStep } = usePlanTotals(clientId);
  const { applications } = useConsumerPlan(clientId);
  if (!selectedQuote) return null;
  // With an application already sent, applying for this option is an explicit new application; the
  // one already sent is never replaced or changed.
  const isNewApplication = applications.length > 0;

  const start = () => {
    saveAnswers(answers);
    goTo('application-personal');
  };

  const steps: ChecklistItem[] = [
    { marker: 'done', title: 'Your answers carry forward', description: 'Review the details you’ve already shared.' },
    {
      marker: 'next',
      title: `Fill in ${sections.length} short sections`,
      description: `${sections.map(section => APPLICATION_SECTION_TITLES[section]).join(', ')}. Skip anything you don’t know.`,
    },
    { marker: 'next', title: 'Our team checks it before submission', description: 'We look for errors and missing details to help avoid delays.' },
  ];

  return (
    <StepFrame
      step="application-start"
      title={isNewApplication ? 'Start a new application' : 'Ready to start your full application?'}
      subtitle={
        isNewApplication
          ? 'The application you already sent stays as it is. We’ll prefill this one from your answers.'
          : 'We’ll prefill what we can from your intake.'
      }
      onBack={() => back(summaryStep)}
      help={
        <>
          <p>Your application gives our team what we need to coordinate underwriting with the insurance company. We review missing or inconsistent details first to help avoid delays.</p>
          <p>A different carrier or product may need more questions or new signatures. Reusing answers doesn’t transfer approval.</p>
          <div className="flex flex-col items-start">
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('options')}>
              Explore a different coverage option
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={openIntercomChat}>
              Ask our team a question
            </Button>
          </div>
        </>
      }
      primary={{ label: isNewApplication ? 'Start a new application' : 'Start my full application', onClick: start }}
      next="start with your personal details."
    >
      <SelectedCoverageSummary quote={selectedQuote} />
      <Checklist items={steps} />
      <p className="text-muted-foreground text-sm">
        Change your mind before coverage is issued? We’ll reuse as many answers as possible for another application.
      </p>
    </StepFrame>
  );
}
